import { useState, useMemo, useEffect, Fragment, useRef } from 'react';
import { useApp } from '../../contexts/AppContext';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useTenant } from '../../contexts/TenantContext';
import { BarChart3, Package, Warehouse, Radio, Repeat, ClipboardList, Truck, Download, Wrench } from 'lucide-react';

const fmtTs = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString('en-MY', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

export default function ReportsPage() {
  const { assets, facilities, requests, checkouts, movements } = useApp();
  const { features } = useTenant();
  const f = features || {};
  const { profile } = useAuth();

  const [report, setReport]   = useState('summary');
  const [exported, setExported] = useState('');

  const [rfidMovements, setRfidMovements] = useState([]);
  const [rfidLoading, setRfidLoading]     = useState(false);
  const [rfidError, setRfidError]         = useState('');
  const [rfidLoaded, setRfidLoaded]       = useState(false);
  const [rfidRange, setRfidRange]         = useState('30d');
  const [customFrom, setCustomFrom]       = useState(new Date(Date.now() - 30*86400_000).toISOString().slice(0,10));
  const [customTo,   setCustomTo]         = useState(new Date().toISOString().slice(0,10));
  const [rfidSearch, setRfidSearch]       = useState('');
  const [rfidFacility, setRfidFacility]   = useState('all');
  const [expandedAsset, setExpandedAsset] = useState(null);
  const [pdfExporting, setPdfExporting]   = useState(false);
  const reportRef = useRef(null);

  // Maintenance report state
  const [maintHistory, setMaintHistory] = useState([]);
  const [maintLoading, setMaintLoading] = useState(false);
  const [maintLoaded,  setMaintLoaded]  = useState(false);

  const rangeLabel = rfidRange === '7d' ? 'Last 7 Days'
    : rfidRange === '30d' ? 'Last 30 Days'
    : rfidRange === '90d' ? 'Last 3 Months'
    : rfidRange === 'custom' ? `${customFrom} → ${customTo}` : rfidRange;

  const exportPDF = async () => {
    if (!reportRef.current) return;
    setPdfExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { default: jsPDF } = await import('jspdf');
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#060b10', scale: 2, useCORS: true, logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth  = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth   = pageWidth;
      const imgHeight  = (canvas.height * imgWidth) / canvas.width;
      let heightLeft   = imgHeight;
      let position     = 0;
      pdf.setFillColor(6, 11, 16);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.setFillColor(6, 11, 16);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`wms_${activeReport}_${rfidRange}_${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (e) { alert('PDF export failed: ' + e.message); }
    finally { setPdfExporting(false); }
  };

  const loadRfidMovements = async (range = rfidRange, from = customFrom, to = customTo) => {
    if (!profile?.org_id) return;
    setRfidLoading(true); setRfidError('');
    let since, until;
    if (range === 'custom') {
      if (!from || !to) { setRfidError('Pick start and end dates'); setRfidLoading(false); return; }
      if (new Date(from) > new Date(to)) { setRfidError('Start date must be before end date'); setRfidLoading(false); return; }
      since = new Date(from + 'T00:00:00').toISOString();
      until = new Date(to   + 'T23:59:59').toISOString();
    } else {
      const days = { '7d': 7, '30d': 30, '90d': 90 }[range] || 30;
      since = new Date(Date.now() - days * 86400_000).toISOString();
    }
    const rpc = await supabase.rpc('sample_movements', {
      p_org_id: profile.org_id,
      p_since: since,
      p_until: until || null,
      p_max_rows: 50000,
    });
    const data = rpc.data, error = rpc.error;
    if (data) data.sort((a, b) => new Date(b.detected_at) - new Date(a.detected_at));
    if (error) setRfidError(error.message);
    else { setRfidMovements(data || []); setRfidLoaded(true); }
    setRfidLoading(false);
  };

  const loadMaintenance = async () => {
    if (!profile?.org_id) return;
    setMaintLoading(true);
    let since;
    if (rfidRange === 'custom') since = new Date(customFrom + 'T00:00:00').toISOString();
    else {
      const days = { '7d': 7, '30d': 30, '90d': 90 }[rfidRange] || 30;
      since = new Date(Date.now() - days * 86400_000).toISOString();
    }
    const { data } = await supabase
      .from('maintenance_history')
      .select('*, assets(name, type)')
      .eq('org_id', profile.org_id)
      .gte('performed_at', since)
      .order('performed_at', { ascending: false });
    setMaintHistory(data || []);
    setMaintLoaded(true);
    setMaintLoading(false);
  };

  useEffect(() => {
    const usesRange = ['summary', 'assets', 'facility', 'rfid'].includes(report);
    if (usesRange && !rfidLoaded) loadRfidMovements();
    if (report === 'maintenance') { setMaintLoaded(false); loadMaintenance(); }
  }, [report, rfidRange, customFrom, customTo]); // eslint-disable-line

  const rfidByAsset = useMemo(() => {
    const map = new Map();
    for (const m of rfidMovements) {
      const key = m.asset_id || m.epc || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          key, asset_id: m.asset_id, asset_name: m.asset_name || 'Unknown', epc: m.epc,
          scanCount: 0, lastSeen: m.detected_at,
          lastFacility: m.to_facility_name || m.to_facility,
          movements: [], facilities: new Set(),
        });
      }
      const g = map.get(key);
      g.scanCount += 1;
      g.movements.push(m);
      if (m.to_facility_name) g.facilities.add(m.to_facility_name);
      if (new Date(m.detected_at) > new Date(g.lastSeen)) {
        g.lastSeen = m.detected_at;
        g.lastFacility = m.to_facility_name || m.to_facility;
      }
    }
    return Array.from(map.values()).sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen));
  }, [rfidMovements]);

  const scansPerDay = useMemo(() => {
    const buckets = new Map();
    let startMs, endMs;
    if (rfidRange === 'custom' && customFrom && customTo) {
      startMs = new Date(customFrom + 'T00:00:00').getTime();
      endMs   = new Date(customTo   + 'T23:59:59').getTime();
    } else {
      const days = { '7d': 7, '30d': 30, '90d': 90 }[rfidRange] || 30;
      endMs   = Date.now();
      startMs = endMs - (days - 1) * 86400_000;
    }
    for (let t = startMs; t <= endMs; t += 86400_000) buckets.set(new Date(t).toISOString().slice(0, 10), 0);
    for (const m of rfidMovements) {
      const day = m.detected_at?.slice(0, 10);
      if (buckets.has(day)) buckets.set(day, buckets.get(day) + 1);
    }
    return Array.from(buckets.entries()).map(([day, count]) => ({ day: day.slice(5), count }));
  }, [rfidMovements, rfidRange, customFrom, customTo]);

  const scansPerHour = useMemo(() => {
    const buckets = Array.from({ length: 24 }, (_, h) => ({ hour: `${h}:00`, count: 0 }));
    for (const m of rfidMovements) buckets[new Date(m.detected_at).getHours()].count++;
    return buckets;
  }, [rfidMovements]);

  const topAssetsScanned = useMemo(() => {
    const map = new Map();
    for (const m of rfidMovements) {
      const key = m.asset_id || m.epc; if (!key) continue;
      const prev = map.get(key) || { name: m.asset_name || 'Unknown', count: 0 };
      prev.count++; map.set(key, prev);
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [rfidMovements]);

  const assetsByStatus = useMemo(() => {
    const seenIds = new Set();
    for (const m of rfidMovements) if (m.asset_id) seenIds.add(m.asset_id);
    const map = new Map();
    assets.filter(a => seenIds.has(a.id)).forEach(a => {
      map.set(a.status || 'Unknown', (map.get(a.status || 'Unknown') || 0) + 1);
    });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [assets, rfidMovements]);

  const movementsByFacility = useMemo(() => {
    const map = new Map();
    for (const m of rfidMovements) {
      const key = m.to_facility_name || m.to_facility || 'Unknown';
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map.entries()).map(([name, count]) => ({
      name: name.length > 12 ? name.slice(0, 12) + '…' : name, count,
    })).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [rfidMovements]);

  const filteredRfid = useMemo(() => {
    let list = rfidByAsset;
    if (rfidFacility !== 'all') list = list.filter(g => g.facilities.has(rfidFacility));
    if (rfidSearch) {
      const q = rfidSearch.toLowerCase();
      list = list.filter(g => (g.asset_name || '').toLowerCase().includes(q) || (g.epc || '').toLowerCase().includes(q));
    }
    return list;
  }, [rfidByAsset, rfidSearch, rfidFacility]);

  const stats = useMemo(() => ({
    total:           assets.length,
    available:       assets.filter(a => a.status === 'Available').length,
    inUse:           assets.filter(a => a.status === 'In Use').length,
    maintenance:     assets.filter(a => a.status === 'Under Maintenance').length,
    inTransit:       assets.filter(a => a.status === 'In Transit').length,
    totalValue:      assets.reduce((s, a) => s + parseFloat(a.value || 0), 0),
    pendingReqs:     f.can_requests !== false ? requests.filter(r => r.status === 'Pending').length : null,
    activeCheckouts: f.can_checkout !== false ? checkouts.filter(c => c.status === 'Out').length    : null,
    totalScans:      movements.length,
  }), [assets, requests, checkouts, movements, f]);

  const REPORTS = [
    { id: 'summary',    label: <span style={{display:'inline-flex',alignItems:'center',gap:6}}><BarChart3 size={14}/>Summary Report</span>,   always: true },
    { id: 'assets',     label: <span style={{display:'inline-flex',alignItems:'center',gap:6}}><Package size={14}/>Asset Inventory</span>,   always: true },
    { id: 'checkouts',  label: <span style={{display:'inline-flex',alignItems:'center',gap:6}}><Repeat size={14}/>Checkout Report</span>,    feature: 'can_checkout' },
    { id: 'requests',   label: <span style={{display:'inline-flex',alignItems:'center',gap:6}}><ClipboardList size={14}/>Transfer Requests</span>, feature: 'can_requests' },
    { id: 'deliveries', label: <span style={{display:'inline-flex',alignItems:'center',gap:6}}><Truck size={14}/>Deliveries</span>,          feature: 'can_deliveries' },
    { id: 'facility',   label: <span style={{display:'inline-flex',alignItems:'center',gap:6}}><Warehouse size={14}/>Facility Report</span>,    always: true },
    { id: 'maintenance',label: <span style={{display:'inline-flex',alignItems:'center',gap:6}}><Wrench size={14}/>Maintenance</span>,       feature: 'can_maintenance' },
    { id: 'rfid',       label: <span style={{display:'inline-flex',alignItems:'center',gap:6}}><Radio size={14}/>RFID Movements</span>,     feature: 'can_realtime' },
  ].filter(r => r.always || f[r.feature] !== false);

  const visibleIds = REPORTS.map(r => r.id);
  const activeReport = visibleIds.includes(report) ? report : 'summary';

  const exportCSV = (data, filename, headers) => {
    const rows = [headers.map(h => h.label), ...data.map(row => headers.map(h => JSON.stringify(row[h.key] || '')))];
    const csv  = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = filename + '.csv'; a.click();
    URL.revokeObjectURL(url);
    setExported(filename); setTimeout(() => setExported(''), 2000);
  };

  const ExportBar = ({ data, filename, cols }) => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
      <button className="btn" style={{ background: '#0d1520', border: '1px solid #1a2a3a', color: '#00e5a0', fontSize: 12 }}
        onClick={() => exportCSV(data, filename, cols)}><Download size={13} style={{verticalAlign:'-2px',marginRight:6}}/>Export CSV</button>
      <button className="btn" style={{ background: '#0d1520', border: '1px solid #1a2a3a', color: '#5b9cf6', fontSize: 12 }}
        onClick={() => { const blob = new Blob([JSON.stringify(data,null,2)],{type:'application/json'}); const url=URL.createObjectURL(blob); const a=document.createElement('a');a.href=url;a.download=filename+'.json';a.click(); setExported(filename); setTimeout(()=>setExported(''),2000); }}><Download size={13} style={{verticalAlign:'-2px',marginRight:6}}/>Export JSON</button>
      <button className="btn" style={{ background: '#0d1520', border: '1px solid #1a2a3a', color: '#b89de8', fontSize: 12 }} onClick={() => window.print()}>🖨 Print</button>
      {exported === filename && <span style={{ color: '#00e5a0', fontSize: 12, display: 'flex', alignItems: 'center' }}>✓ Exported!</span>}
    </div>
  );

  const assetCols = [
    { key:'id', label:'Asset ID' }, { key:'inventory_number', label:'Inventory No' },
    { key:'epc', label:'EPC' }, { key:'name', label:'Name' }, { key:'type', label:'Type' },
    { key:'status', label:'Status' }, { key:'current_facility_name', label:'Facility' },
  ];

  const scansPerAsset = useMemo(() => {
    const map = new Map();
    for (const m of rfidMovements) {
      const key = m.asset_id || m.epc; if (!key) continue;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [rfidMovements]);

  const scansPerFacility = useMemo(() => {
    const map = new Map();
    for (const m of rfidMovements) {
      const key = m.to_facility || m.to_facility_name; if (!key) continue;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [rfidMovements]);

  return (
    <div className="fadein">
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: '#e8edf2' }}>Reports & Export</div>
        <div style={{ fontSize: 12, color: '#7090a0', marginTop: 4 }}>Generate, view and export data reports</div>
      </div>

      <div style={{ display:'flex',gap:6,marginBottom:14,flexWrap:'wrap',padding:6,background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12 }}>
        {REPORTS.map(r => (
          <button key={r.id} onClick={() => setReport(r.id)} style={{
            padding:'8px 16px', fontSize:12,
            fontWeight: activeReport === r.id ? 700 : 500,
            border:'none', borderRadius:8, cursor:'pointer',
            background: activeReport === r.id ? '#00e5a015' : 'transparent',
            color:      activeReport === r.id ? '#00e5a0'   : '#7090a0',
          }}
          onMouseEnter={e => { if (activeReport !== r.id) e.currentTarget.style.background = '#1a2a3a'; }}
          onMouseLeave={e => { if (activeReport !== r.id) e.currentTarget.style.background = 'transparent'; }}>
            {r.label}
          </button>
        ))}
      </div>

      <div style={{ display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:20,padding:'10px 14px',background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12 }}>
        <span style={{ fontSize:11,color:'#7090a0',fontWeight:600,letterSpacing:'1px',textTransform:'uppercase' }}>Date Range:</span>
        {[['7d','7 days'],['30d','30 days'],['90d','3 months'],['custom','Custom']].map(([r,label]) => {
          const active = rfidRange === r;
          return (
            <button key={r} onClick={() => { setRfidRange(r); if (r !== 'custom') loadRfidMovements(r); }} disabled={rfidLoading}
              style={{ fontSize:11,padding:'6px 12px',borderRadius:6,
                background: active ? '#5b9cf618' : 'transparent',
                border: `1px solid ${active ? '#5b9cf640' : '#1a2a3a'}`,
                color: active ? '#5b9cf6' : '#a0b4c0',
                cursor: rfidLoading ? 'wait' : 'pointer', fontWeight: active ? 700 : 500 }}>
              {label}
            </button>
          );
        })}
        {rfidRange === 'custom' && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:6,padding:'5px 8px',fontSize:11,color:'#c8d6e0',colorScheme:'dark' }}/>
            <span style={{ fontSize:11,color:'#7090a0' }}>to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:6,padding:'5px 8px',fontSize:11,color:'#c8d6e0',colorScheme:'dark' }}/>
            <button onClick={() => loadRfidMovements('custom', customFrom, customTo)} disabled={rfidLoading}
              style={{ fontSize:11,padding:'5px 12px',borderRadius:6,background:'#00e5a018',border:'1px solid #00e5a040',color:'#00e5a0',cursor: rfidLoading?'wait':'pointer',fontWeight:700 }}>Apply</button>
          </>
        )}
        {rfidLoading && <span style={{ fontSize:11,color:'#f5a623',marginLeft:'auto' }}>Loading…</span>}
        {!rfidLoading && rfidLoaded && (
          <span style={{ fontSize:11,color:'#7090a0',marginLeft:'auto' }}>
            {rfidMovements.length.toLocaleString()} movements in range
            <span style={{ marginLeft:8, color:'#5b9cf6' }} title="1 row per asset per hour">📊 sampled</span>
          </span>
        )}
        <button onClick={exportPDF} disabled={pdfExporting}
          style={{ fontSize:11,padding:'6px 12px',borderRadius:6,background:'#e0575718',border:'1px solid #e0575740',color:'#e05757',cursor:pdfExporting?'wait':'pointer',fontWeight:700,display:'inline-flex',alignItems:'center',gap:6 }}>
          {pdfExporting ? 'Generating…' : '📄 Print PDF'}
        </button>
      </div>

      <div>
        <div ref={reportRef} style={{ background:'#060b10' }}>
          {pdfExporting && (
            <div style={{ padding:'20px 24px',borderBottom:'2px solid #1a2a3a',marginBottom:20 }}>
              <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:'#e8edf2' }}>Trekify WMS Report</div>
              <div style={{ fontSize:12,color:'#7090a0',marginTop:4 }}>{REPORTS.find(r=>r.id===activeReport)?.label} · {rangeLabel} · Generated {new Date().toLocaleString('en-MY')}</div>
            </div>
          )}

          {/* SUMMARY */}
          {activeReport === 'summary' && (
            <div>
              <ExportBar data={[{ ...stats, date_range: rfidRange, movements_in_range: rfidMovements.length }]} filename={`wms_summary_${rfidRange}`} cols={[
                ...Object.keys(stats).map(k => ({ key: k, label: k })),
                { key:'date_range', label:'Date Range' },
                { key:'movements_in_range', label:'Movements in Range' },
              ]} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
                <StatCard label="Total Assets"   value={stats.total}      icon="📦" accent="#00e5a0" sub="All registered" />
                <StatCard label="Available"      value={stats.available}  icon="✅" accent="#00e5a0" sub={`${((stats.available/stats.total||0)*100).toFixed(0)}% of total`} />
                <StatCard label="In Use"         value={stats.inUse}      icon="📤" accent="#5b9cf6" sub="Currently out" />
                <StatCard label="Maintenance"    value={stats.maintenance} icon="🔧" accent="#f5a623" sub="Under repair" />
                <StatCard label="Total Value"    value={`RM ${(stats.totalValue/1000).toFixed(1)}K`} icon="💰" accent="#b89de8" sub="Estimated" />
                <StatCard label="RFID Scans"     value={stats.totalScans} icon="📡" accent="#00e5a0" sub="All movements" />
                {stats.pendingReqs !== null && <StatCard label="Pending Requests" value={stats.pendingReqs} icon="⏳" accent="#f5a623" sub="Awaiting approval" />}
                {stats.activeCheckouts !== null && <StatCard label="Active Checkouts" value={stats.activeCheckouts} icon="🔄" accent="#5b9cf6" sub="Currently out" />}
              </div>

              <div style={{ marginBottom:20 }}>
                <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,color:'#e8edf2',marginBottom:12 }}>Analytics — {rangeLabel}</div>

                <ChartCard title="RFID Scans per Day" sub={`${rfidMovements.length.toLocaleString()} sampled scans`}>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={scansPerDay}>
                      <CartesianGrid stroke="#1a2a3a" strokeDasharray="3 3"/>
                      <XAxis dataKey="day" tick={{ fontSize:10,fill:'#7090a0' }} axisLine={{ stroke:'#1a2a3a' }}/>
                      <YAxis tick={{ fontSize:10,fill:'#7090a0' }} axisLine={{ stroke:'#1a2a3a' }}/>
                      <Tooltip contentStyle={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:8,fontSize:12 }} labelStyle={{ color:'#c8d6e0' }}/>
                      <Line type="monotone" dataKey="count" stroke="#00e5a0" strokeWidth={2} dot={{ r:3,fill:'#00e5a0' }} activeDot={{ r:5 }}/>
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>

                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14 }}>
                  <ChartCard title="Assets by Status" sub={`${assetsByStatus.reduce((s,x)=>s+x.value,0)} seen in range`}>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={assetsByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                          {assetsByStatus.map((_,i) => <Cell key={i} fill={['#00e5a0','#5b9cf6','#f5a623','#b89de8','#e05757','#7090a0'][i%6]}/>)}
                        </Pie>
                        <Tooltip contentStyle={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:8,fontSize:12 }}/>
                        <Legend wrapperStyle={{ fontSize:11,color:'#a0b4c0' }}/>
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  <ChartCard title="Peak Activity Hours" sub="Scans by hour of day">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={scansPerHour}>
                        <CartesianGrid stroke="#1a2a3a" strokeDasharray="3 3"/>
                        <XAxis dataKey="hour" tick={{ fontSize:9,fill:'#7090a0' }} axisLine={{ stroke:'#1a2a3a' }} interval={2}/>
                        <YAxis tick={{ fontSize:10,fill:'#7090a0' }} axisLine={{ stroke:'#1a2a3a' }}/>
                        <Tooltip contentStyle={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:8,fontSize:12 }} labelStyle={{ color:'#c8d6e0' }}/>
                        <Bar dataKey="count" fill="#5b9cf6" radius={[4,4,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>

                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14 }}>
                  <ChartCard title="Top 10 Most-Scanned Assets" sub="Movement frequency">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={topAssetsScanned} layout="vertical" margin={{ left:80 }}>
                        <CartesianGrid stroke="#1a2a3a" strokeDasharray="3 3"/>
                        <XAxis type="number" tick={{ fontSize:10,fill:'#7090a0' }} axisLine={{ stroke:'#1a2a3a' }}/>
                        <YAxis type="category" dataKey="name" tick={{ fontSize:10,fill:'#c8d6e0' }} axisLine={{ stroke:'#1a2a3a' }} width={80}/>
                        <Tooltip contentStyle={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:8,fontSize:12 }} labelStyle={{ color:'#c8d6e0' }}/>
                        <Bar dataKey="count" fill="#b89de8" radius={[0,4,4,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  <ChartCard title="Movements by Facility" sub="Where activity happens">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={movementsByFacility}>
                        <CartesianGrid stroke="#1a2a3a" strokeDasharray="3 3"/>
                        <XAxis dataKey="name" tick={{ fontSize:10,fill:'#7090a0' }} axisLine={{ stroke:'#1a2a3a' }} angle={-15} textAnchor="end" height={60}/>
                        <YAxis tick={{ fontSize:10,fill:'#7090a0' }} axisLine={{ stroke:'#1a2a3a' }}/>
                        <Tooltip contentStyle={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:8,fontSize:12 }} labelStyle={{ color:'#c8d6e0' }}/>
                        <Bar dataKey="count" fill="#f5a623" radius={[4,4,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>
              </div>

              <div className="card">
                <div style={{ fontSize: 11, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1.2px', fontWeight: 600, marginBottom: 16 }}>Assets by Facility</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: '1px solid #1a2a3a' }}>
                    {['Facility','Type','Total','Available','In Use','Maintenance'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {facilities.map(fac => {
                      const fa = assets.filter(a => a.current_facility === fac.id || a.currentFacility === fac.id);
                      return (
                        <tr key={fac.id} style={{ borderBottom: '1px solid #1a2a3a' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: '#e8edf2' }}>{fac.name}</td>
                          <td style={{ padding: '10px 12px', color: '#a0b4c0', fontSize: 12 }}>{fac.type || '—'}</td>
                          <td style={{ padding: '10px 12px', fontFamily: "'IBM Plex Mono',monospace" }}>{fa.length}</td>
                          <td style={{ padding: '10px 12px', color: '#00e5a0', fontFamily: "'IBM Plex Mono',monospace" }}>{fa.filter(a => a.status === 'Available').length}</td>
                          <td style={{ padding: '10px 12px', color: '#5b9cf6', fontFamily: "'IBM Plex Mono',monospace" }}>{fa.filter(a => a.status === 'In Use').length}</td>
                          <td style={{ padding: '10px 12px', color: '#f5a623', fontFamily: "'IBM Plex Mono',monospace" }}>{fa.filter(a => a.status === 'Under Maintenance').length}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ASSETS */}
          {activeReport === 'assets' && (
            <div>
              {(() => {
                const assetsWithScans = assets.map(a => ({
                  ...a, scans_in_range: (scansPerAsset.get(a.id) || scansPerAsset.get(a.epc) || 0),
                }));
                return (<>
                  <ExportBar data={assetsWithScans} filename={`wms_assets_${rfidRange}`} cols={[...assetCols, { key:'scans_in_range', label:'Scans in Range' }]} />
                  <SimpleTable rows={assetsWithScans} cols={[
                    { key: 'inventory_number', label: 'Inventory No', render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: '#f5a623' }}>{v}</span> },
                    { key: 'name',  label: 'Asset', render: v => <span style={{ fontWeight: 600, color: '#e8edf2' }}>{v}</span> },
                    { key: 'type',  label: 'Type',  render: v => <span style={{ fontSize: 12 }}>{v}</span> },
                    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
                    { key: 'current_facility_name', label: 'Facility', render: v => <span style={{ fontSize: 12, color: '#a0b4c0' }}>{v || '—'}</span> },
                    { key: 'scans_in_range', label: 'Scans in Range', render: v => <span style={{ fontSize:12,fontWeight:700,color: v > 0 ? '#00e5a0':'#7090a0',fontFamily:"'IBM Plex Mono',monospace" }}>{v}</span> },
                    { key: 'epc',   label: 'EPC',   render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: '#7090a0' }}>{v}</span> },
                  ]} />
                </>);
              })()}
            </div>
          )}

          {/* CHECKOUTS */}
          {activeReport === 'checkouts' && f.can_checkout !== false && (
            <div>
              <ExportBar data={checkouts} filename="wms_checkouts" cols={[
                {key:'id',label:'ID'},{key:'asset_name',label:'Asset'},{key:'checked_out_by',label:'By'},
                {key:'checked_out_to',label:'Purpose'},{key:'checked_out_at',label:'Out At'},{key:'returned_at',label:'Returned'},{key:'status',label:'Status'},
              ]} />
              <SimpleTable rows={checkouts} cols={[
                { key: 'asset_name',      label: 'Asset',     render: v => <span style={{ fontWeight: 600, color: '#e8edf2' }}>{v}</span> },
                { key: 'checked_out_by',  label: 'By' },
                { key: 'checked_out_to',  label: 'Purpose' },
                { key: 'checked_out_at',  label: 'Out At',    render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: '#7090a0' }}>{fmtTs(v)}</span> },
                { key: 'returned_at',     label: 'Returned',  render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: '#00e5a0' }}>{fmtTs(v) || '—'}</span> },
                { key: 'status',          label: 'Status',    render: v => <StatusBadge status={v} /> },
              ]} />
            </div>
          )}

          {/* REQUESTS */}
          {activeReport === 'requests' && f.can_requests !== false && (
            <div>
              <ExportBar data={requests} filename="wms_requests" cols={[
                {key:'id',label:'ID'},{key:'asset_name',label:'Asset'},{key:'from_facility_name',label:'From'},
                {key:'to_facility_name',label:'To'},{key:'status',label:'Status'},{key:'requested_by',label:'By'},
              ]} />
              <SimpleTable rows={requests} cols={[
                { key: 'asset_name',        label: 'Asset',    render: v => <span style={{ fontWeight: 600, color: '#e8edf2' }}>{v}</span> },
                { key: 'from_facility_name',label: 'From' },
                { key: 'to_facility_name',  label: 'To' },
                { key: 'priority',          label: 'Priority', render: v => <span style={{ fontWeight: 600, fontSize: 12, color: v==='Urgent'?'#e05757':v==='High'?'#f5a623':'#5b9cf6' }}>{v}</span> },
                { key: 'status',            label: 'Status',   render: v => <StatusBadge status={v} /> },
                { key: 'requested_by',      label: 'By' },
              ]} />
            </div>
          )}

          {/* DELIVERIES */}
          {activeReport === 'deliveries' && f.can_deliveries !== false && (
            <div>
              <ExportBar data={[]} filename="wms_deliveries" cols={[{key:'id',label:'ID'}]} />
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#7090a0' }}>No delivery records yet.</div>
            </div>
          )}

          {/* FACILITY */}
          {activeReport === 'facility' && (
            <div>
              {(() => {
                const facWithScans = facilities.map(fx => ({
                  ...fx,
                  assets_count: assets.filter(a => a.current_facility === fx.id || a.currentFacility === fx.id).length,
                  scans_in_range: (scansPerFacility.get(fx.id) || 0) + (scansPerFacility.get(fx.name) || 0),
                }));
                return (<>
                  <ExportBar data={facWithScans} filename={`wms_facilities_${rfidRange}`} cols={[
                    {key:'id',label:'ID'},{key:'name',label:'Name'},{key:'type',label:'Type'},{key:'status',label:'Status'},
                    {key:'assets_count',label:'Assets'},{key:'scans_in_range',label:'Scans in Range'},
                  ]} />
                  <SimpleTable rows={facilities} cols={[
                    { key: 'name',   label: 'Facility', render: v => <span style={{ fontWeight: 600, color: '#e8edf2' }}>{v}</span> },
                    { key: 'type',   label: 'Type' },
                    { key: 'status', label: 'Status',   render: v => <StatusBadge status={v} /> },
                    { key: 'id',     label: 'Assets',   render: (_, row) => {
                      const count = assets.filter(a => a.current_facility === row.id || a.currentFacility === row.id).length;
                      return <span style={{ fontFamily: "'IBM Plex Mono',monospace", color: '#00e5a0' }}>{count}</span>;
                    }},
                    { key: 'id',     label: 'Scans in Range',   render: (_, row) => {
                      const count = (scansPerFacility.get(row.id) || 0) + (scansPerFacility.get(row.name) || 0);
                      return <span style={{ fontFamily: "'IBM Plex Mono',monospace",fontWeight:700,color: count > 0 ? '#5b9cf6' : '#7090a0' }}>{count}</span>;
                    }},
                  ]} />
                </>);
              })()}
            </div>
          )}

          {/* MAINTENANCE */}
          {activeReport === 'maintenance' && f.can_maintenance !== false && (
            <div>
              {(() => {
                const now = new Date();
                const soon = new Date(now); soon.setDate(soon.getDate() + 30);
                const overdue = assets.filter(a => a.next_maintenance_date && new Date(a.next_maintenance_date) < now && a.status !== 'Decommissioned');
                const dueSoon = assets.filter(a => a.next_maintenance_date && new Date(a.next_maintenance_date) >= now && new Date(a.next_maintenance_date) <= soon && a.status !== 'Decommissioned');
                const totalCost = maintHistory.reduce((s, h) => s + parseFloat(h.cost || 0), 0);
                const avgCost = maintHistory.length ? totalCost / maintHistory.length : 0;
                const costByType = new Map();
                maintHistory.forEach(h => {
                  const type = h.assets?.type || 'Unknown';
                  costByType.set(type, (costByType.get(type) || 0) + parseFloat(h.cost || 0));
                });
                const typeData = Array.from(costByType.entries()).map(([name, cost]) => ({ name, cost })).sort((a,b) => b.cost - a.cost);
                return (<>
                  <ExportBar data={maintHistory.map(h => ({...h, asset_name: h.assets?.name, asset_type: h.assets?.type}))} filename={`wms_maintenance_${rfidRange}`} cols={[
                    {key:'performed_at',label:'Date'},{key:'asset_name',label:'Asset'},{key:'asset_type',label:'Type'},
                    {key:'performed_by',label:'By'},{key:'notes',label:'Notes'},{key:'cost',label:'Cost'},{key:'next_date',label:'Next Date'},
                  ]} />

                  <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20 }}>
                    <StatCard label="Overdue"       value={overdue.length} icon="🔴" accent="#e05757" sub="Needs immediate action" />
                    <StatCard label="Due Soon"      value={dueSoon.length} icon="⚠️" accent="#f5a623" sub="Within 30 days" />
                    <StatCard label="Logs in Range" value={maintHistory.length} icon="🔧" accent="#5b9cf6" sub={rangeLabel} />
                    <StatCard label="Total Cost"    value={`RM ${totalCost.toFixed(2)}`} icon="💰" accent="#00e5a0" sub={`Avg: RM ${avgCost.toFixed(2)}`} />
                  </div>

                  {typeData.length > 0 && (
                    <ChartCard title="Maintenance Cost by Asset Type" sub={`${maintHistory.length} logs`}>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={typeData}>
                          <CartesianGrid stroke="#1a2a3a" strokeDasharray="3 3"/>
                          <XAxis dataKey="name" tick={{ fontSize:10,fill:'#7090a0' }} axisLine={{ stroke:'#1a2a3a' }}/>
                          <YAxis tick={{ fontSize:10,fill:'#7090a0' }} axisLine={{ stroke:'#1a2a3a' }}/>
                          <Tooltip contentStyle={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:8,fontSize:12 }} formatter={(v) => `RM ${v.toFixed(2)}`}/>
                          <Bar dataKey="cost" fill="#00e5a0" radius={[4,4,0,0]}/>
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  )}

                  {overdue.length > 0 && (
                    <div className="card" style={{ padding:16, marginBottom:14, border:'1px solid #e0575740' }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#e05757', marginBottom:10 }}>🔴 Overdue Maintenance</div>
                      <SimpleTable rows={overdue} cols={[
                        { key:'name', label:'Asset', render:v=><span style={{ fontWeight:600,color:'#e8edf2' }}>{v}</span> },
                        { key:'type', label:'Type' },
                        { key:'current_facility_name', label:'Facility' },
                        { key:'next_maintenance_date', label:'Due Date', render:v => <span style={{ color:'#e05757',fontWeight:700 }}>{v}</span> },
                        { key:'maintained_by', label:'Maintained By' },
                      ]}/>
                    </div>
                  )}

                  <div style={{ fontSize:13, fontWeight:700, color:'#c8d6e0', marginTop:16, marginBottom:10 }}>📋 Maintenance Log History</div>
                  {maintLoading ? <div style={{ padding:20, textAlign:'center', color:'#7090a0' }}>Loading…</div> :
                   maintHistory.length === 0 ? <div style={{ padding:30, textAlign:'center', color:'#7090a0', fontSize:13, background:'#090e15', border:'1px solid #1a2a3a', borderRadius:12 }}>No maintenance logs in this range</div> :
                   <SimpleTable rows={maintHistory} cols={[
                     { key:'performed_at', label:'Date', render:v=><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'#a0b4c0' }}>{fmtTs(v)}</span> },
                     { key:'assets', label:'Asset', render:(_,row)=><span style={{ fontWeight:600, color:'#e8edf2' }}>{row.assets?.name || row.asset_id}</span> },
                     { key:'assets', label:'Type', render:(_,row)=><span style={{ fontSize:12, color:'#a0b4c0' }}>{row.assets?.type || '—'}</span> },
                     { key:'performed_by', label:'By' },
                     { key:'notes', label:'Notes' },
                     { key:'cost', label:'Cost', render:v=>v ? <span style={{ color:'#f5a623', fontWeight:600 }}>RM {parseFloat(v).toFixed(2)}</span> : '—' },
                     { key:'next_date', label:'Next Due', render:v=><span style={{ fontSize:11, color:'#5b9cf6' }}>{v || '—'}</span> },
                   ]}/>}
                </>);
              })()}
            </div>
          )}

          {/* RFID MOVEMENTS */}
          {activeReport === 'rfid' && f.can_realtime !== false && (
            <div>
              <div style={{ display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:14,padding:'10px 14px',background:'#060b10',border:'1px solid #1a2a3a',borderRadius:10 }}>
                <input type="text" placeholder="Search asset name or EPC…" value={rfidSearch}
                  onChange={e => setRfidSearch(e.target.value)}
                  style={{ flex:1,minWidth:200,background:'#060b10',border:'1px solid #1a2a3a',borderRadius:6,padding:'6px 10px',fontSize:12,color:'#c8d6e0' }}/>
                <select value={rfidFacility} onChange={e => setRfidFacility(e.target.value)}
                  style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:6,padding:'6px 10px',fontSize:12,color:'#c8d6e0' }}>
                  <option value="all">All Facilities</option>
                  {facilities.map(fac => <option key={fac.id} value={fac.name}>{fac.name}</option>)}
                </select>
                <button onClick={() => loadRfidMovements()} disabled={rfidLoading}
                  style={{ fontSize:11,padding:'5px 10px',borderRadius:6,background:'#1a2a3a',border:'none',color:'#c8d6e0',cursor: rfidLoading?'wait':'pointer' }}>↻ Refresh</button>
              </div>

              {rfidError && (
                <div style={{ padding:10,marginBottom:12,background:'#e0575710',border:'1px solid #e0575730',borderRadius:8,color:'#e05757',fontSize:12 }}>⚠ {rfidError}</div>
              )}

              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10,padding:'0 4px' }}>
                <div style={{ fontSize:12,color:'#7090a0' }}>
                  {rfidLoading ? 'Loading movements…' : (
                    <>Showing <strong style={{ color:'#c8d6e0' }}>{filteredRfid.length}</strong> asset{filteredRfid.length===1?'':'s'} · <strong style={{ color:'#c8d6e0' }}>{rfidMovements.length.toLocaleString()}</strong> sampled scans</>
                  )}
                </div>
                <ExportBar data={rfidMovements} filename={`wms_rfid_movements_${rfidRange}`} cols={[
                  {key:'asset_name',label:'Asset'},{key:'epc',label:'EPC'},{key:'to_facility_name',label:'Facility'},
                  {key:'reader_name',label:'Reader'},{key:'antenna_port',label:'Port'},{key:'antenna_name',label:'Antenna'},{key:'rssi',label:'RSSI'},{key:'detected_at',label:'Time'},
                ]} />
              </div>

              {filteredRfid.length === 0 && !rfidLoading ? (
                <div style={{ textAlign:'center',padding:'50px 0',color:'#7090a0',fontSize:13,background:'#090e15',border:'1px solid #1a2a3a',borderRadius:12 }}>
                  {rfidMovements.length === 0 ? 'No RFID movements in this range' : 'No assets match the filter'}
                </div>
              ) : (
                <div style={{ background:'#090e15',border:'1px solid #1a2a3a',borderRadius:12,overflow:'hidden' }}>
                  <table style={{ width:'100%',borderCollapse:'collapse' }}>
                    <thead style={{ background:'#0d1520' }}>
                      <tr>
                        <th style={{ width:36 }}/>
                        {['Asset','EPC','Last Seen','Last Facility','Scans','Facilities'].map(h => (
                          <th key={h} style={{ textAlign:'left',padding:'10px 12px',fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',fontWeight:700,borderBottom:'1px solid #1a2a3a' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRfid.map(g => {
                        const expanded = expandedAsset === g.key;
                        return (
                          <Fragment key={g.key}>
                            <tr onClick={() => setExpandedAsset(expanded ? null : g.key)}
                              style={{ cursor:'pointer',borderBottom:'1px solid #0d1520',background: expanded ? '#0d1520' : 'transparent' }}>
                              <td style={{ padding:'10px 12px',color:'#7090a0',fontSize:11,textAlign:'center' }}>{expanded ? '▼' : '▶'}</td>
                              <td style={{ padding:'10px 12px',fontSize:12,fontWeight:600,color:'#e8edf2' }}>{g.asset_name}</td>
                              <td style={{ padding:'10px 12px',fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:'#5b9cf6' }}>{g.epc || '—'}</td>
                              <td style={{ padding:'10px 12px',fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'#7090a0' }}>{fmtTs(g.lastSeen)}</td>
                              <td style={{ padding:'10px 12px',fontSize:12,color:'#a0b4c0' }}>{g.lastFacility || '—'}</td>
                              <td style={{ padding:'10px 12px',fontSize:12,fontWeight:700,color:'#00e5a0' }}>{g.scanCount.toLocaleString()}</td>
                              <td style={{ padding:'10px 12px',fontSize:11,color:'#a0b4c0' }}>{g.facilities.size}</td>
                            </tr>
                            {expanded && (
                              <tr>
                                <td colSpan={7} style={{ padding:'0 16px 14px',background:'#0d1520' }}>
                                  {(() => {
                                    const chrono = [...g.movements].sort((a,b) => new Date(a.detected_at) - new Date(b.detected_at));
                                    let moves = 0, shifts = 0;
                                    const rows = chrono.map((m, i) => {
                                      const prev = i > 0 ? chrono[i-1] : null;
                                      const facChanged = prev && (m.to_facility_name || m.to_facility) !== (prev.to_facility_name || prev.to_facility);
                                      const antChanged = prev && !facChanged && String(m.antenna_port) !== String(prev.antenna_port);
                                      if (facChanged) moves++;
                                      if (antChanged) shifts++;
                                      return { ...m, _facChanged: facChanged, _antChanged: antChanged, _prev: prev };
                                    }).reverse();
                                    return (<>
                                      <div style={{ fontSize:11,color:'#7090a0',fontWeight:600,padding:'10px 0',display:'flex',gap:14 }}>
                                        <span>Movement History ({g.movements.length})</span>
                                        {moves > 0 && <span style={{ color:'#e05757',fontWeight:700 }}>↔ {moves} moved</span>}
                                        {shifts > 0 && <span style={{ color:'#f5a623',fontWeight:700 }}>⇄ {shifts} shifted antenna</span>}
                                        <span style={{ color:'#00e5a0' }}>● {chrono.length - moves - shifts} stationary</span>
                                      </div>
                                      <div style={{ maxHeight:320,overflowY:'auto',background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8 }}>
                                        <table style={{ width:'100%',borderCollapse:'collapse' }}>
                                          <thead style={{ position:'sticky',top:0,background:'#060b10' }}>
                                            <tr>
                                              {['Event','Time','Facility','Antenna','RSSI'].map(h => (
                                                <th key={h} style={{ textAlign:'left',padding:'6px 10px',fontSize:9,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',borderBottom:'1px solid #1a2a3a' }}>{h}</th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {rows.map((m,i) => {
                                              const eventColor = m._facChanged ? '#e05757' : m._antChanged ? '#f5a623' : '#00e5a0';
                                              const eventLabel = m._facChanged ? '↔ MOVED' : m._antChanged ? '⇄ SHIFTED' : '● SEEN';
                                              const antName = m.antenna_name || `ANT${m.antenna_port}`;
                                              const prevAnt = m._prev ? (m._prev.antenna_name || `ANT${m._prev.antenna_port}`) : null;
                                              const prevFac = m._prev ? (m._prev.to_facility_name || m._prev.to_facility) : null;
                                              return (
                                                <tr key={i} style={{ borderBottom:'1px solid #0d1520' }}>
                                                  <td style={{ padding:'6px 10px' }}>
                                                    <span style={{ fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:10,background:eventColor+'20',color:eventColor }}>{eventLabel}</span>
                                                  </td>
                                                  <td style={{ padding:'6px 10px',fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:'#a0b4c0' }}>{fmtTs(m.detected_at)}</td>
                                                  <td style={{ padding:'6px 10px',fontSize:11,color:'#c8d6e0' }}>
                                                    {m._facChanged
                                                      ? <><span style={{ color:'#7090a0' }}>{prevFac}</span> <span style={{ color:'#f5a623' }}>→</span> <span style={{ color:'#5b9cf6',fontWeight:600 }}>{m.to_facility_name}</span></>
                                                      : (m.to_facility_name || '—')}
                                                  </td>
                                                  <td style={{ padding:'6px 10px',fontSize:11,color:'#c8d6e0' }}>
                                                    {m._antChanged
                                                      ? <><span style={{ color:'#7090a0' }}>{prevAnt}</span> <span style={{ color:'#f5a623' }}>→</span> <span style={{ color:'#00e5a0',fontWeight:600 }}>{antName}</span></>
                                                      : antName}
                                                  </td>
                                                  <td style={{ padding:'6px 10px',fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:'#b89de8' }}>{m.rssi ? `${m.rssi} dBm` : '—'}</td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </>);
                                  })()}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, sub, children }) {
  return (
    <div className="card" style={{ padding:16,marginBottom:14 }}>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:12 }}>
        <div style={{ fontSize:13,fontWeight:700,color:'#e8edf2' }}>{title}</div>
        {sub && <div style={{ fontSize:10,color:'#7090a0' }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function StatCard({ label, value, sub, accent = '#00e5a0', icon }) {
  return (
    <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: accent, borderRadius: '12px 0 0 12px' }} />
      <div style={{ fontSize: 11, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1.2px', fontWeight: 600, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 28, fontWeight: 800, color: '#e8edf2' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#7090a0', marginTop: 5 }}>{sub}</div>}
      <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 22, opacity: 0.12 }}>{icon}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = { Available:'#00e5a0', 'In Use':'#5b9cf6', 'Under Maintenance':'#f5a623', 'In Transit':'#b89de8', Pending:'#f5a623', Approved:'#00e5a0', Rejected:'#e05757', Completed:'#5b9cf6', Active:'#00e5a0', Out:'#f5a623', Returned:'#00e5a0' };
  const c = colors[status] || '#a0b4c0';
  return <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:600, background:c+'18', color:c, border:`1px solid ${c}38` }}><span style={{ width:5, height:5, borderRadius:'50%', background:c, display:'inline-block' }}/>{status}</span>;
}

function SimpleTable({ rows, cols }) {
  if (!rows || rows.length === 0) return <div style={{ textAlign: 'center', padding: '40px 0', color: '#7090a0' }}>No records found</div>;
  return (
    <div style={{ background: '#090e15', border: '1px solid #1a2a3a', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#060b10', borderBottom: '1px solid #1a2a3a' }}>
              {cols.map(c => <th key={c.label} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 10, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((row, ri) => (
              <tr key={row.id || ri} style={{ borderBottom: '1px solid #0d1520', background: ri % 2 === 0 ? '#090e15' : '#060b10' }}>
                {cols.map((c, ci) => (
                  <td key={ci} style={{ padding: '10px 12px', fontSize: 13 }}>
                    {c.render ? c.render(row[c.key], row) : <span style={{ color: '#8a9aaa' }}>{row[c.key] || '—'}</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '10px 16px', borderTop: '1px solid #1a2a3a', fontSize: 11, color: '#7090a0', fontFamily: "'IBM Plex Mono',monospace" }}>
        {rows.length} records {rows.length > 200 ? '(showing first 200)' : ''}
      </div>
    </div>
  );
}
